import { useEffect, useState } from 'react';
import { CustomLink as Link } from '@/components/custom-link';
import Section from '@/components/integrated/section';
import transformMarkdownToReactHtml from '@/utils/transform-markdown-react-html';
import { FEATURE_TOGGLE_LIST } from '@/context/feature-toggles/feature-toggles.types';
import { useFeatureTogglesContext } from '@/context/feature-toggles/feature-toggles-context';
import { LegalContentComponentProperties } from './legal-content-page.types';
import styles from './legal-content-page.module.scss';

export const mappedPathData = [
  {
    path: '/missing-avios',
    titleKey: 'missingAviosTitle',
    contentKey: 'missingAvios',
  },
  {
    path: '/help-centre',
    titleKey: 'helpCentreTitle',
    contentKey: 'helpCentre',
  },
  {
    path: '/faq',
    titleKey: 'frequentlyAskedQuestionsTitle',
    contentKey: 'frequentlyAskedQuestions',
  },
  {
    path: '/privacy',
    titleKey: 'privacyAndSecurityTitle',
    contentKey: 'privacyAndSecurity',
  },
  {
    path: '/terms',
    titleKey: 'termsAndConditionsTitle',
    contentKey: 'termsAndConditions',
  },
  {
    path: '/cookies',
    titleKey: 'cookiesTitle',
    contentKey: 'cookies',
  },
  {
    path: '/collection-reminder',
    titleKey: 'collectionReminderTitle',
    contentKey: 'collectionReminder',
  },
  {
    path: '/accessibility',
    titleKey: 'accessibilityTitle',
    contentKey: 'accessibility',
  },
];

export const pathDataMap = new Map(
  mappedPathData.map(({ path, titleKey, contentKey }) => [
    path,
    { titleKey, contentKey },
  ]),
);

export const getNavItemProperties = (
  destination: string,
  data: LegalContentComponentProperties['data'],
  activeUrl?: string,
) => {
  const { titleKey, contentKey } = pathDataMap.get(destination) ?? {};

  const ctaText = data[0][titleKey as keyof (typeof data)[0]];
  const isActive = `${destination}/` === activeUrl;

  return { ctaText, isActive, contentKey };
};

const LegalContentPage = ({
  data,
  navigation,
  activeUrl,
}: LegalContentComponentProperties) => {
  const featuresToggles = useFeatureTogglesContext();

  const renderHelpCentre = featuresToggles.includes(
    FEATURE_TOGGLE_LIST.TEMP_SHARED_FOOTER,
  );

  const filteredNavigationItems = navigation.filter(({ destination }) => {
    if (!renderHelpCentre) pathDataMap.delete('/help-centre');
    return pathDataMap.has(destination);
  });

  const navList = filteredNavigationItems.map(({ destination }) => {
    const { ctaText, isActive } = getNavItemProperties(
      destination,
      data,
      activeUrl,
    );

    if (!ctaText) return null;

    return (
      <Link
        className={`${styles['page-legal__navigation-list-item']} ${
          isActive ? styles['page-legal__navigation-list-item--active'] : ''
        }`}
        key={destination}
        href={destination}
        aria-selected={Boolean(isActive)}
      >
        {ctaText}
      </Link>
    );
  });

  const { contentKey } = getNavItemProperties(activeUrl.slice(0, -1), data);
  const content = data[0][contentKey as keyof (typeof data)[0]];
  const [sectionAria, setSectionAria] = useState('');

  useEffect(() => {
    const headingOne = document.querySelector('h1');
    setSectionAria(headingOne?.textContent ?? '');
  }, [activeUrl]);
  return (
    <Section className={styles['page-legal']}>
      <section aria-label={data[0].navAriaLabel}>
        <div className={styles['page-legal__navigation-list']}>{navList}</div>
      </section>
      <section aria-label={sectionAria}>
        <div className={styles['page-legal__content']}>
          {transformMarkdownToReactHtml(content)}
        </div>
        {contentKey === 'helpCentre' && data[0].contactUs && (
          <div className={styles['page-legal__content']}>
            {transformMarkdownToReactHtml(data[0].contactUs)}
          </div>
        )}
      </section>
    </Section>
  );
};

export default LegalContentPage;
